<template>
  <dialog-scroll-lock
    width="420" content-class="relative contain-none overflow-visible"
    :value="value"
    @input="$emit('input', false)"
  >

    <error-dialog :status="loadExistingResponseStatus" />
    <error-dialog :status="saveStatus" />

    <sign-in-dialog hide-subscribe v-model="showVerify">
      <template v-slot:title>
        Verify your email address<template v-if="verified?.startsWith('+')"> / phone number</template>
      </template>
      <template v-slot:subtitle>
        The host has requested that all responses come from verified email addresses<template v-if="verified?.startsWith('+')">. You can also continue using the phone number to which this invitation was originally sent.</template>
        <template v-if="verified">
          <br><br>
          <v-btn class="btn-like-google-sign-in primary white--text elevation-2" outlined @click="target = verified">
            <v-icon v-html="mdiArrowRightBoldCircle" />
            Continue with {{verified | email-phone}}
          </v-btn>
        </template>
      </template>
    </sign-in-dialog>

    <public-guest-list-dialog v-if="showGuestList" :form="event.form" :guests="guestList" v-model="showGuestList" />

    <add-to-calendar-dialog v-if="showAddToCalendar" :event="event" v-model="showAddToCalendar" />

    <v-btn
      v-if="isSample"
      style="z-index: 1; top: -12px; left: -12px; width: unset;"
      absolute rounded large color="primary"
      @click="$emit('edit-event')"
    >
      <v-icon class="mr-2" v-html="mdiCalendarEdit" />
      Edit event…
    </v-btn>

    <v-card class="py-5 subtitle overflow-auto" style="max-height: calc(100svh - 48px) !important;">
      <v-btn class="float-right ma-3 mt-n2" icon @click="$emit('input', false)">
        <v-icon v-html="mdiClose" />
      </v-btn>

      <event-summary
        class="pt-5 pb-9 px-6"
        :event="event"
        :totals="totals"
        :totals-loading="loadGuestListStatus.pending"
        @click-calendar="showAddToCalendar = true"
        @click-guest-list="showGuestList = true"
      />

      <v-divider />

      <div class="pt-5 px-6 text-left">
        <v-expand-transition>
          <div v-if="tab == 0">
            <div class="title black--text">Respond as</div>
            <div v-if="event.allowed == 'INVITED'" v-bind:class="{ 'error--text': !target }">
              Responses for this event are only allowed from links sent via email / text
            </div>

            <!-- Note: you may want this to be disabled when event.allowed is set to INVITED. Not doing so runs the risk of unexpected names showing up in the guest list, but I feel like guests will want the ability to edit their names for things like typos. -->
            <v-text-field
              class="my-3"
              hide-details placeholder="Name" outlined dense
              :disabled="(event.allowed == 'INVITED') && !target"
              :rules="[ v => !!v ]"
              v-model="name"
            />
            <v-text-field
              v-if="event.verify || (event.allowed == 'INVITED')"
              hide-details placeholder="Email address / phone number" outlined dense readonly
              :disabled="event.allowed == 'INVITED'"
              :value="target"
              @click="showVerify = true"
            />
            <email-phone-field v-else required hide-contacts v-model="target" />
            <div class="mt-9 text-right">
              <v-btn
                class="pr-2"
                color="primary"
                :disabled="!name || !target"
                :loading="loadExistingResponseStatus.pending"
                @click="loadExistingResponse"
              >
                Continue
                <v-icon v-html="mdiChevronRight" />
              </v-btn>
            </div>
            <v-expand-transition>
              <div v-if="previous" class="pt-6 text--label-active font-italic text-right">
                Response for {{target | email-phone}} last updated <span style="white-space: nowrap;">{{previous.updated_at | moment('short')}}</span>
              </div>
            </v-expand-transition>
          </div>
        </v-expand-transition>

        <v-expand-transition>
          <div v-if="tab == 1">
            <div>
              <v-btn class="pl-2" color="primary" @click="tab = 0">
                <v-icon v-html="mdiChevronLeft" />
                Respond as
              </v-btn>
            </div>

            <div v-if="event.allowed == 'NONE'" class="mt-9 warning--text font-italic">
              <v-icon small color="warning" v-html="mdiAlert" /> The host has closed responses for this event
            </div>

            <!-- It might be nice to confirm the name / target -->
            <!--
            <div class="mt-9 title black--text">Name</div>
            <div class="mb-3">
              <div>{{name}} – <i>{{target | email-phone}}</i></div>
              <div v-if="previous">Last updated</div>
            </div>
            -->

            <v-form ref="rsvp" @submit.prevent>
              <form-rsvp
                class="text-left"
                :form="event.form"
                :response="response"
                :disabled="saveStatus.pending || (event.allowed == 'NONE')"
              />
            </v-form>
            <div v-if="event.allowed != 'NONE'" class="mt-9 text-right">
              <v-btn color="primary" :loading="saveStatus.pending" @click="save">
                {{previous ? 'Update' : 'Submit'}}
              </v-btn>
            </div>
            <div v-if="previous" class="mt-6 text--label-active font-italic text-right">
              Response for {{target | email-phone}} last updated <span style="white-space: nowrap;">{{previous.updated_at | moment('short')}}</span>
            </div>
          </div>
        </v-expand-transition>
      </div>

    </v-card>
  </dialog-scroll-lock>
</template>


<style scoped>
.title {
  line-height: 1.2em;
  color: rgba(0, 0, 0, 0.6);
}
.title.required:after {
  content: ' *';
  color: #ff5252;
  vertical-align: top;
}
.subtitle {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 20px;
}
</style>


<script>
import AddToCalendarDialog from '@/components/AddToCalendarDialog.vue';
import EmailPhoneField from '@/components/EmailPhoneField.vue';
import EventSummary from '@/components/EventSummary.vue';
import FormRsvp from '@/components/FormRsvp.vue';
import PublicGuestListDialog from '@/components/PublicGuestListDialog.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import { patchResponse } from '@/utils/event-rsvp-form.js';
import storage from '@/utils/local-storage.js';
import stripHTML from '@/utils/strip-html.js';
import iconPathChild from 'raw-loader!@/assets/icon-path-child.txt';
import { mdiAccountGroup, mdiAlert, mdiCalendarPlus, mdiCalendarEdit, mdiClose, mdiHumanMale, mdiTextBox, mdiTextBoxCheck, mdiMapMarker, mdiMonitorAccount, mdiOpenInNew, mdiChevronRight, mdiMessage, mdiAt, mdiLogout, mdiAccountCheck, mdiArrowRightBoldCircle, mdiChevronLeft } from '@mdi/js';


export default {
  name: 'form-rsvp-recipient-dialog',

  components: {
    AddToCalendarDialog,
    EmailPhoneField,
    EventSummary,
    FormRsvp,
    PublicGuestListDialog,
    SignInDialog
  },

  props:[ 'value', 'token', 'order', 'preview', 'isSample' ],

  data() {
    return {
      tab: 0,
      verified: null,
      showVerify: false,
      name: null,
      target: null,
      previous: null,
      response: null,
      dirty: false,
      guestList: [],
      filter: '',
      showAddToCalendar: false,
      showGuestList: false,
      loadGuestListStatus: this.$track('loadGuestList'),
      loadExistingResponseStatus: this.$track('loadExistingResponse'),
      saveStatus: this.$track('save')
    };
  },

  computed: {
    event() {
      return this.order.event;
    },

    totals() {
      const totals = { 'Attending': 0, 'Adults': 0, 'Children': 0 };
      for (const row of this.guestList)
        for (const key of [ 'Attending', 'Adults', 'Children' ])
          totals[key] += this.getFieldValue(row, key) || 0;
      return totals;
    }
  },

  created() {
    Object.assign(this, { iconPathChild, mdiAccountGroup, mdiAlert, mdiCalendarPlus, mdiCalendarEdit, mdiClose, mdiHumanMale, mdiTextBox, mdiTextBoxCheck, mdiMapMarker, mdiMonitorAccount, mdiOpenInNew, mdiChevronRight, mdiMessage, mdiAt, mdiLogout, mdiAccountCheck, mdiArrowRightBoldCircle, mdiChevronLeft });
  },

  methods: {
    async loadGuestList() {
      if (!this.event.public_guest_list)
        return;
      this.guestList = (await this.$call('get-public-form-responses', { token: this.token })) || [];
    },

    getFieldValue(row, key) {
      // TODO: this is largely duplicated in a few other places - consolidate
      try {
        const r = row.response || {};
        const { request_names, separate_counts } = this.event.form;
        switch (key) {
          case 'Attending':
            if (!r['Can you make it?'])
              return null;
            if (r['Can you make it?'] == 'No')
              return 0;
            if (request_names && r['Attendees'])
              return r['Attendees'].filter(x => x.name).length;
            if (_.isObject(r['Total number of attendees']))
              return Object.values(r['Total number of attendees']).reduce((a, b) => a + b, 0);
            return r['Total number of attendees'] || 1;
          case 'Adults':
            if (!r['Can you make it?'])
              return null;
            if (r['Can you make it?'] == 'No')
              return 0;
            if (request_names && r['Attendees'])
              return r['Attendees'].filter(x => x.name && (x.type != 'child')).length;
            if (_.isObject(r['Total number of attendees']))
              return r['Total number of attendees'].adult;
            return r['Total number of attendees'] || 1;
          case 'Children':
            if (!r['Can you make it?'])
              return null;
            if (r['Can you make it?'] == 'No')
              return 0;
            if (request_names && r['Attendees'])
              return r['Attendees'].filter(x => x.name && (x.type == 'child')).length;
            if (_.isObject(r['Total number of attendees']))
              return r['Total number of attendees'].child;
            return null;
        }
      } catch (e) {
        return null;
      }
    },

    async loadExistingResponse() {
      if (this.preview)
        throw new VisibleError(`You can't submit responses when previewing an invitation`);

      if (this.isSample)
        throw new VisibleError(`You can't submit responses for sample invitations`);

      if (this.order.state == 'PENDING')
        throw new VisibleError(`You can't submit responses for this invitation yet – please complete checkout first`);

      storage.set('rsvp-target-' + this.token, this.target);
      const params = { token: this.token, [ this.target.includes('@') ? 'email' : 'phone' ]: this.target };
      const r = await this.$call('get-public-form-response', params);
      this.previous = r;
      this.response = patchResponse(this.event.form, r?.response || {});

      await this.$nextTick();
      this.dirty = false;
      if (this.name)
        this.tab = 1;
    },

    async save() {
      if (!this.$refs.rsvp.validate())
        throw new VisibleError('Please fix the highlighted errors');

      if (this.order.sample)
        throw new VisibleError(`You can't submit responses for sample invitations`);

      if (this.order.state == 'PENDING')
        throw new VisibleError(`You can't submit responses for this invitation yet – please complete checkout first`);

      this.previous = await this.$call(
        'update-form-response',
        {
          token: this.token,
          response: this.response,
          name: this.name,
          [ this.target.includes('@') ? 'email' : 'phone' ]: this.target
        }
      );

      this.dirty = false;
      this.$notify('Response saved');

      this.loadGuestList();
    }
  },

  watch: {
    token: {
      immediate: true,
      handler(v) {
        const r = this.order.verified_recipient;
        this.verified = r && (r.phone || r.email);
        this.target = (this.order.event.allowed == 'INVITED') ? this.verified : storage.get('rsvp-target-' + v) || this.verified || this.global.profile?.email;
        this.name = stripHTML(r?.to || '');

        this.loadGuestList();
        if (this.target)
          this.loadExistingResponse();
      }
    },

    response: {
      deep: true,
      handler() {
        this.dirty = true;
      }
    },

    'global.profile.email'(v, prev) {
      if (v) {
        // The only reason we would have signed in in this context would be set the target
        this.target = v;
        this.loadExistingResponse();
      } else if (this.target && (this.target == prev)) {
        // If we signed out and the target matches our previous profile, then the target is no longer verified and we
        // can't use it
        this.target = null;
      }
    }
  }
}
</script>
